<template>
  <el-dialog v-model="visible" :title="title" width="80%">
    <el-form>
      <el-form-item label="Document Title">
        <el-input v-model="documentTitle" />
      </el-form-item>
      <el-form-item label="Document Type">
        <el-select v-model="documentType" placeholder="Select document type">
          <el-option
            v-for="type in proofTypes"
            :key="type.proofTypeId"
            :label="type.laDesc || type.enDesc"
            :value="type.proofTypeId"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Document Name">
        <el-input v-model="documentName" />
      </el-form-item>
      <el-form-item label="Reference No">
        <el-input v-model="referenceNo" :disabled="true" />
      </el-form-item>
      <el-form-item label="Note (Optional)">
        <el-input v-model="noteNo" type="textarea" :rows="2" />
      </el-form-item>
      <el-form-item label="Attach File">
        <input 
          type="file" 
          @change="onFileChange" 
          :accept="fileTypeUtil.getAcceptAttribute()"
          style="width: 100%; padding: 8px; border: 1px solid #dcdfe6; border-radius: 4px;"
        />
        <div style="font-size: 12px; color: #666; margin-top: 4px;">
          Accepted formats: Documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, RTF, TXT, CSV, ZIP, RAR, ODT, ODS, ODP) and Images (JPG, PNG, GIF, BMP, WEBP, SVG, TIFF) - max 10MB
        </div>
        <div v-if="file" style="margin-top: 8px;">
          <strong>Selected:</strong> {{ file.name }} ({{ formatFileSize(file.size) }})
          <div v-if="fileError" style="color: red;">{{ fileError }}</div>
        </div>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="saveDocument" :loading="uploading">Save & Upload</el-button>
      </el-form-item>
      <div v-if="uploadSuccess" style="color: green; margin-top: 10px;">
        Document uploaded successfully!
      </div>
      <div v-if="existingDocuments && existingDocuments.length > 0" class="uploaded-documents">
        <h4>Previously Uploaded Documents ({{ existingDocuments.length }})</h4>
        <div class="document-list">
          <div
            v-for="doc in existingDocuments"
            :key="doc.id || doc.Docid"
            class="document-item"
          >
            <div class="document-info">
              <el-icon><Document /></el-icon>
              <div class="document-details">
                <span class="document-name">{{ doc.documentName || doc.Docname || doc.fileName }}</span>
                <span class="document-type">{{ doc.documentType || doc.Docprooftype }}</span>
              </div>
            </div>
            <div class="document-actions">
              <el-button size="small" type="success" @click="downloadDocument(doc)">
                <el-icon><Download /></el-icon>
                Download
              </el-button>
              <el-button size="small" type="danger" @click="deleteDocument(doc)">
                <el-icon><Delete /></el-icon>
                Delete
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-form>
  </el-dialog>
</template>

<script>
import { ref, watch, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import storageService from '@/services/storageService'
import fileDownloadUtil from '@/utils/fileDownload'
import fileTypeUtil from '@/utils/fileTypes'

export default {
  name: 'DocumentUpload',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: 'Upload Document' },
    module: { type: String, default: '' },
    proofTypeGroup: { type: String, default: '' },
    referenceNo: { type: [String, Number], default: '' },
    userId: { type: [String, Number], default: '' },
    proofTypes: { type: Array, default: () => [] },
    existingDocuments: { type: Array, default: () => [] },
    isNewSubmission: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'uploaded', 'document-deleted'],
  setup(props, { emit }) {
    console.log('DocumentUpload props received:', {
      userId: props.userId,
      referenceNo: props.referenceNo,
      proofTypes: props.proofTypes,
      isNewSubmission: props.isNewSubmission
    })
    
    const visible = ref(props.modelValue)
    watch(() => props.modelValue, v => visible.value = v)
    watch(visible, v => emit('update:modelValue', v))

    const documentTitle = ref('')
    const documentType = ref('')
    const documentName = ref('')
    const referenceNo = ref('')
    const noteNo = ref('')
    const file = ref(null)
    const fileError = ref('')
    const uploading = ref(false)
    const uploadSuccess = ref(false)

    // Set referenceNo to prop when it changes
    watch(() => props.referenceNo, (newVal) => {
      console.log('DocumentUpload referenceNo changed:', newVal)
      referenceNo.value = newVal ? String(newVal) : ''
    }, { immediate: true })

    // Watch proofTypes to log when they change
    watch(() => props.proofTypes, (newVal) => {
      console.log('DocumentUpload proofTypes changed:', newVal)
    }, { immediate: true })

    // Reset form when dialog opens
    watch(visible, (newVal) => {
      if (newVal) {
        console.log('DocumentUpload dialog opened, resetting form')
        resetForm()
      }
    })

    const resetForm = () => {
      documentTitle.value = ''
      documentType.value = ''
      documentName.value = ''
      noteNo.value = ''
      file.value = null
      fileError.value = ''
      uploadSuccess.value = false
      
      // Clear the file input element
      const fileInput = document.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.value = ''
      }
    }

    // Filter proofTypes by group LEAVE
    const filteredProofTypes = computed(() => {
      if (!props.proofTypes) return []
      return props.proofTypes.filter(type => type.proofGroup === 'LEAVE')
    })

    const allowedTypes = [
      // Document formats
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/rtf',
      'text/plain',
      'text/csv',
      'application/xml',
      'application/json',
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/x-tar',
      'application/x-gzip',
      
      // Image formats
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'image/svg+xml',
      'image/tiff',
      'image/tif',
      'image/x-icon',
      'image/vnd.microsoft.icon',
      
      // Additional document formats
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/vnd.apple.pages',
      'application/vnd.apple.numbers',
      'application/vnd.apple.keynote',
      'application/vnd.google-apps.document',
      'application/vnd.google-apps.spreadsheet',
      'application/vnd.google-apps.presentation'
    ]
    
    // File extensions for fallback validation
    const allowedExtensions = [
      // Documents
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'rtf', 'txt', 'csv',
      'xml', 'json', 'zip', 'rar', '7z', 'tar', 'gz', 'odt', 'ods', 'odp',
      'pages', 'numbers', 'key',
      
      // Images
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico'
    ]
    const maxFileSize = 10 * 1024 * 1024 // 10MB

    const onFileChange = (event) => {
      fileError.value = ''
      const f = event.target.files[0]
      if (!f) {
        file.value = null
        return
      }
      
      console.log('File selected:', {
        name: f.name,
        type: f.type,
        size: f.size,
        extension: f.name.split('.').pop()?.toLowerCase()
      })
      
      // Use file type utility for comprehensive validation
      const validation = fileTypeUtil.validateFile(f, maxFileSize)
      
      if (!validation.valid) {
        fileError.value = validation.error
        file.value = null
        return
      }
      
      file.value = f
      console.log('File validation passed:', f.name, 'Type:', validation.info.typeName)
      
      // Auto-fill document name from file name (without extension)
      if (!documentName.value) {
        const fileNameWithoutExtension = f.name.replace(/\.[^/.]+$/, '')
        documentName.value = fileNameWithoutExtension
      }
      
      // Auto-fill document title if empty
      if (!documentTitle.value) {
        documentTitle.value = f.name
      }
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const saveDocument = async () => {
      uploadSuccess.value = false
      
      console.log('saveDocument validation check:', {
        documentTitle: documentTitle.value,
        documentType: documentType.value,
        documentName: documentName.value,
        referenceNo: referenceNo.value,
        file: file.value,
        fileError: fileError.value
      })
      
      if (!documentTitle.value || !documentType.value || !documentName.value || !referenceNo.value || !file.value) {
        console.log('Validation failed - missing required fields')
        ElMessage.error('Please fill all required fields and select a file.')
        return
      }
      if (fileError.value) {
        console.log('Validation failed - file error:', fileError.value)
        ElMessage.error(fileError.value)
        return
      }
      
      console.log('Validation passed, starting upload...')
      uploading.value = true
      try {
        // Real upload logic, match backend model exactly
        const payload = {
          UserId: parseInt(props.userId) || 0,
          Doctitle: documentTitle.value,
          Docdate: new Date().toISOString(),
          Docname: documentName.value,
          Doctype: parseInt(documentType.value) || 0,
          Referenceno: referenceNo.value,
          Noteno: noteNo.value,
          Files: file.value
        }
        console.log('Upload payload:', payload);
        // Pass individual parameters to storageService instead of options object
        const uploadResult = await storageService.uploadFile(file.value, {
          documentType: parseInt(documentType.value) || 0,
          userId: parseInt(props.userId) || 0,
          documentName: documentTitle.value,
          description: noteNo.value,
          referenceNo: referenceNo.value,
          note: noteNo.value
        })
        if (uploadResult && uploadResult.success) {
          uploadSuccess.value = true
          ElMessage.success(props.isNewSubmission ? 'Documents uploaded successfully! You can now track your leave request.' : 'Document uploaded!')
          emit('uploaded', uploadResult.Model)
          if (props.isNewSubmission) {
            visible.value = false // Close dialog for new submissions
          }
        } else {
          ElMessage.error(uploadResult?.ErrorMessage || 'Upload failed')
        }
      } catch (e) {
        ElMessage.error(e.message || 'Upload failed')
      } finally {
        uploading.value = false
      }
    }

    const downloadDocument = async (doc) => {
      try {
        console.log('Downloading document:', doc.docname, 'URL:', doc.fileUrl)
        const result = await fileDownloadUtil.downloadFile({
          Docid: doc.docid,
          Docname: doc.docname,
          Docextension: doc.docextension,
          FileUrl: doc.fileUrl,
          storagetype: doc.storagetype
        })
        console.log('Download result:', result)
        if (result.success) {
          ElMessage.success('Document downloaded successfully')
        } else {
          ElMessage.error(result.error || 'Download failed')
        }
      } catch (e) {
        console.error('Download error:', e)
        ElMessage.error(e.message || 'Download failed')
      }
    }

    const deleteDocument = async (doc) => {
      try {
        await ElMessageBox.confirm(
          'Are you sure you want to delete this document?',
          'Confirm Deletion',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        )
        await storageService.deleteFile(doc)
        ElMessage.success('Document deleted!')
        emit('document-deleted', doc)
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error(e.message || 'Delete failed')
        }
      }
    }

    return {
      visible,
      documentTitle,
      documentType,
      documentName,
      referenceNo,
      noteNo,
      file,
      fileError,
      uploading,
      uploadSuccess,
      onFileChange,
      saveDocument,
      formatFileSize,
      downloadDocument,
      deleteDocument,
      resetForm,
      fileTypeUtil,
      proofTypes: props.proofTypes // Expose proofTypes to template
    }
  }
}
</script>

<style scoped>
.document-upload-dialog {
  padding: 20px 0;
}

.document-upload-section {
  margin-top: 20px;
}

.document-form {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.file-attachment-section {
  margin-bottom: 16px;
}

.file-help-text {
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  margin-bottom: 0;
}

.selected-files {
  margin-top: 16px;
}

.selected-files h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.file-item .el-icon {
  color: #1c75bc;
  font-size: 18px;
}

.file-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.file-size {
  font-size: 12px;
  color: #666;
  background: #f8f9fa;
  padding: 2px 8px;
  border-radius: 12px;
}

.remove-file-btn {
  padding: 4px 8px;
}

.uploaded-documents {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.uploaded-documents h4 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}
</style> 